// PasswordStrength — four-segment meter with a guidance hint under it
// (per design: "Weak — add a number, capital, or symbol"). Pure display;
// renders nothing while the password is empty.

import { useTranslation } from 'react-i18next';
import { scorePassword } from '@/utils/password';

export interface PasswordStrengthProps {
  password: string;
  className?: string;
}

type StrengthLevel = 'weak' | 'fair' | 'good' | 'strong';

// score (0–4) → level, indexed directly.
const LEVELS: StrengthLevel[] = ['weak', 'weak', 'fair', 'good', 'strong'];

const PasswordStrength = ({ password, className = '' }: PasswordStrengthProps) => {
  const { t } = useTranslation();

  if (!password) return null;

  const s = scorePassword(password);
  const level = LEVELS[s];
  const filled = Math.max(1, s);
  const classes = ['app-password-strength', `app-password-strength--${level}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} role='status'>
      <span className='app-password-strength__segments' aria-hidden='true'>
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={[
              'app-password-strength__segment',
              i <= filled && 'app-password-strength__segment--filled',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </span>
      <span className='app-password-strength__hint'>{t(`password_strength_${level}`)}</span>
    </span>
  );
};

export default PasswordStrength;
